import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Card,
  CardBody,
  Code,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  Tooltip,
  useClipboard,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FiCopy, FiMoon, FiSun } from 'react-icons/fi'
import { AdminMfaSetupPanel } from '@/components/account/AdminMfaSetupPanel'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/useToast'
import { authService } from '@/services/authService'
import { AdminAuthBusyError } from '@/utils/apiClient'
import type { AdminMfaSetupResponse, LoginCredentials, LoginResponse } from '@/types'
import { TEXT_CONSTRAINTS } from '@shared/inputConstraints'
import { validateTextWithConstraint } from '@shared/textConstraintHelpers'

const emailConstraint = TEXT_CONSTRAINTS['user.email']
const passwordConstraint = TEXT_CONSTRAINTS['user.password']

type LoginFormData = LoginCredentials & {
  backupCode: string
}

type LoginFlow = 'credentials' | 'recovery' | 'setup' | 'backup'

export function LoginPage() {
  const { t } = useTranslation()
  const [flow, setFlow] = useState<LoginFlow>('credentials')
  const [isLoading, setIsLoading] = useState(false)
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null)
  const [setup, setSetup] = useState<AdminMfaSetupResponse | null>(null)
  const [backupCode, setBackupCode] = useState('')
  const [pendingLogin, setPendingLogin] = useState<LoginResponse | null>(null)
  const { login, completeLogin } = useAuth()
  const toast = useToast()
  const backupClipboard = useClipboard(backupCode)
  const { colorMode, toggleColorMode } = useColorMode()
  const bg = useColorModeValue('gray.50', 'gray.900')
  const cardBg = useColorModeValue('white', 'gray.700')
  const headingColor = useColorModeValue('admin.600', 'admin.300')
  const topBarBg = useColorModeValue('white', 'gray.800')
  const topBarBorder = useColorModeValue('gray.200', 'gray.700')
  const recoveryLinkColor = useColorModeValue('admin.500', 'admin.200')
  const recoveryLinkHoverColor = useColorModeValue('admin.700', 'admin.100')
  const brandLogoText = (import.meta.env.VITE_BRAND_LOGO_TEXT || 'Stafiel').toUpperCase()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
      totp: '',
      backupCode: '',
    },
  })

  const buildConstraintValidator = (field: 'email' | 'password') => (value: string) => {
    const trimmed = typeof value === 'string' ? value.trim() : ''
    if (!trimmed) {
      return field === 'email' ? t('auth.emailRequired') : t('auth.passwordRequired')
    }

    const constraint = field === 'email' ? emailConstraint : passwordConstraint
    const result = validateTextWithConstraint(value, constraint, { requiredOverride: true })
    if (!result.valid) {
      return field === 'email'
        ? t('auth.invalidEmail')
        : t('auth.passwordMinLength', { min: passwordConstraint.minLength ?? 8 })
    }
    return true
  }

  const sanitizeCredentials = (data: LoginFormData) => {
    const email = validateTextWithConstraint(data.email, emailConstraint, {
      requiredOverride: true,
    })
    const password = validateTextWithConstraint(data.password, passwordConstraint, {
      requiredOverride: true,
    })
    if (!email.valid || !email.value || !password.valid || !password.value) {
      return null
    }
    return { email: email.value, password: password.value }
  }

  const beginSetup = async (token: string) => {
    const nextSetup = await authService.setupMfa(token)
    setEnrollmentToken(token)
    setSetup(nextSetup)
    setFlow('setup')
  }

  const handleCredentialsSubmit = async (data: LoginFormData) => {
    const credentials = sanitizeCredentials(data)
    if (!credentials) {
      toast.error({ titleKey: 'auth.loginFailed', descriptionKey: 'auth.invalidCredentials' })
      return
    }

    setIsLoading(true)
    try {
      const result = await login(credentials.email, credentials.password, data.totp?.trim())
      if ('mfaSetupRequired' in result) {
        await beginSetup(result.enrollmentToken)
        return
      }
      toast.success({ titleKey: 'auth.loginSuccess' })
    } catch (error) {
      toast.error({
        titleKey: 'auth.loginFailed',
        descriptionKey: error instanceof AdminAuthBusyError
          ? 'auth.previousSessionPending' : 'auth.mfa.invalidCredentialsOrCode',
        duration: 6000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleRecoverySubmit = async (data: LoginFormData) => {
    const credentials = sanitizeCredentials(data)
    if (!credentials || !data.backupCode.trim()) {
      toast.error({
        titleKey: 'auth.mfa.recoveryFailed',
        descriptionKey: 'auth.mfa.recoveryInvalid',
      })
      return
    }

    setIsLoading(true)
    try {
      const result = await authService.recoverLogin({
        ...credentials,
        backupCode: data.backupCode.trim(),
      })
      await beginSetup(result.enrollmentToken)
    } catch (error) {
      toast.error({
        titleKey: 'auth.mfa.recoveryFailed',
        descriptionKey: error instanceof AdminAuthBusyError
          ? 'auth.previousSessionPending' : 'auth.mfa.recoveryInvalid',
        duration: 6000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmSetup = async (totp: string) => {
    if (!setup || !enrollmentToken) {
      return
    }
    setIsLoading(true)
    try {
      const result = await authService.confirmMfa({
        pendingToken: setup.pendingToken,
        enrollmentToken,
        totp,
      })
      setBackupCode(result.backupCode)
      setPendingLogin(result.login)
      setSetup(null)
      setEnrollmentToken(null)
      setFlow('backup')
    } catch (error) {
      toast.error({
        titleKey: 'auth.mfa.setupFailed',
        descriptionKey: error instanceof AdminAuthBusyError
          ? 'auth.previousSessionPending' : 'auth.mfa.invalidCode',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCompleteEnrollment = () => {
    if (!pendingLogin) {
      return
    }
    try {
      completeLogin(pendingLogin)
      setPendingLogin(null)
      setBackupCode('')
      toast.success({ titleKey: 'auth.loginSuccess' })
    } catch {
      resetRestrictedFlow()
      toast.error({ titleKey: 'auth.loginFailed', descriptionKey: 'auth.sessionChanged' })
    }
  }

  const resetRestrictedFlow = () => {
    setFlow('credentials')
    setEnrollmentToken(null)
    setSetup(null)
    setBackupCode('')
    setPendingLogin(null)
  }

  const renderForm = () => {
    if (flow === 'setup' && setup) {
      return (
        <AdminMfaSetupPanel
          setup={setup}
          isLoading={isLoading}
          onConfirm={handleConfirmSetup}
          onCancel={resetRestrictedFlow}
        />
      )
    }

    if (flow === 'backup') {
      return (
        <Stack spacing="5">
          <Alert status="warning" alignItems="flex-start">
            <AlertIcon />
            <AlertDescription fontSize="xs">
              {t('auth.mfa.backupCodeWarning')}
            </AlertDescription>
          </Alert>
          <HStack>
            <Code flex="1" px="4" py="3" fontSize="sm" overflowWrap="anywhere">
              {backupCode}
            </Code>
            <Tooltip
              label={backupClipboard.hasCopied ? t('common.copySuccess') : t('common.copy')}
            >
              <IconButton
                aria-label={t('auth.mfa.copyBackupCode')}
                icon={<FiCopy />}
                onClick={backupClipboard.onCopy}
                variant="outline"
              />
            </Tooltip>
          </HStack>
          <Button colorScheme="admin" onClick={handleCompleteEnrollment}>
            {t('auth.mfa.savedBackupCode')}
          </Button>
        </Stack>
      )
    }

    const isRecovery = flow === 'recovery'
    return (
      <form
        onSubmit={handleSubmit(
          isRecovery ? handleRecoverySubmit : handleCredentialsSubmit
        )}
      >
        <Stack spacing="5">
          <FormControl isInvalid={!!errors.email}>
            <FormLabel htmlFor="email">{t('auth.email')}</FormLabel>
            <Input
              id="email"
              placeholder={t('auth.emailPlaceholder')}
              type="email"
              autoComplete="username"
              maxLength={emailConstraint.maxLength}
              {...register('email', { validate: buildConstraintValidator('email') })}
            />
            <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.password}>
            <FormLabel htmlFor="password">{t('auth.password')}</FormLabel>
            <Input
              id="password"
              placeholder={t('auth.passwordPlaceholder')}
              type="password"
              autoComplete="current-password"
              maxLength={passwordConstraint.maxLength}
              {...register('password', {
                validate: buildConstraintValidator('password'),
              })}
            />
            <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
          </FormControl>

          {isRecovery ? (
            <FormControl>
              <FormLabel htmlFor="backupCode">{t('auth.mfa.backupCode')}</FormLabel>
              <Input
                id="backupCode"
                autoComplete="off"
                placeholder={t('auth.mfa.backupCodePlaceholder')}
                {...register('backupCode')}
              />
            </FormControl>
          ) : (
            <FormControl>
              <FormLabel htmlFor="totp">{t('auth.mfa.code')}</FormLabel>
              <Input
                id="totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={t('auth.mfa.codePlaceholder')}
                maxLength={6}
                {...register('totp', {
                  setValueAs: (value: string) => value.replace(/\D/g, '').slice(0, 6),
                })}
              />
            </FormControl>
          )}

          <Button
            type="submit"
            colorScheme="admin"
            size="lg"
            fontSize="md"
            isLoading={isLoading}
            loadingText={isRecovery ? t('auth.mfa.recovering') : t('auth.signingIn')}
          >
            {isRecovery ? t('auth.mfa.recover') : t('auth.signIn')}
          </Button>

          <Button
            variant="unstyled"
            size="sm"
            onClick={() => setFlow(isRecovery ? 'credentials' : 'recovery')}
            alignSelf="center"
            bg="transparent"
            borderWidth="0"
            borderStyle="none"
            h="auto"
            minH="0"
            outline="0"
            p="0"
            color={recoveryLinkColor}
            fontSize="sm"
            fontWeight="semibold"
            lineHeight="normal"
            textDecoration="none"
            textUnderlineOffset="2px"
            _hover={{
              bg: 'transparent',
              borderColor: 'transparent',
              borderWidth: 0,
              boxShadow: 'none',
              color: recoveryLinkHoverColor,
              outline: 0,
              textDecoration: 'underline',
            }}
            _active={{
              bg: 'transparent',
              borderColor: 'transparent',
              borderWidth: 0,
              boxShadow: 'none',
              outline: 0,
            }}
            _focus={{
              borderWidth: 0,
              boxShadow: 'none',
              outline: 0,
            }}
            _focusVisible={{
              borderWidth: 0,
              boxShadow: 'none',
              outline: 0,
            }}
          >
            {isRecovery ? t('auth.mfa.backToLogin') : t('auth.mfa.lostAuthenticator')}
          </Button>
        </Stack>
      </form>
    )
  }

  return (
    <Flex minH="100dvh" bg={bg} direction="column">
      <Flex
        height="12"
        align="center"
        justify="space-between"
        px={{ base: 4, md: 6 }}
        bg={topBarBg}
        borderBottomWidth="1px"
        borderBottomColor={topBarBorder}
      >
        <Text
          fontSize="lg"
          fontFamily="monospace"
          fontWeight="bold"
          color={headingColor}
          textTransform="uppercase"
          noOfLines={1}
        >
          {brandLogoText} ADMIN
        </Text>
        <HStack spacing="2">
          <LanguageSwitcher />
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={t('header.toggleColorMode')}
            icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
            onClick={toggleColorMode}
          />
        </HStack>
      </Flex>

      <Flex flex="1" align="center" justify="center">
        <Container maxW="lg" py={{ base: '8', md: '16' }}>
          <Stack spacing="6">
            <Heading size="sm" color={headingColor} textAlign="center">
              {t('auth.loginTitle', { brand: brandLogoText })}
            </Heading>
            <Card bg={cardBg} borderRadius="md">
              <CardBody p={{ base: '5', sm: '8' }}>{renderForm()}</CardBody>
            </Card>
          </Stack>
        </Container>
      </Flex>
    </Flex>
  )
}
